import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAppContext } from "@/components/DolphinLayout";
import {
  ArrowLeft,
  FloppyDisk,
  ArrowsClockwise,
  Globe,
  MapPin,
  Gear,
  Folder,
  DownloadSimple,
  CurrencyCircleDollar,
  ClockCounterClockwise as History,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  "America/New_York", "America/Los_Angeles", "America/Chicago",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Asia/Tokyo", "Asia/Shanghai", "Asia/Singapore",
  "Australia/Sydney",
];

const PLATFORMS = ["Win32", "MacIntel", "Linux x86_64"];
const PROXY_TYPES = ["http", "https", "socks4", "socks5"];

const ProfileEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshStats, refreshFolders, folders } = useAppContext();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [downloadingCashinout, setDownloadingCashinout] = useState(false);
  const [parsingOzon, setParsingOzon] = useState(false);
  const [parseDays, setParseDays] = useState(null);

  const [profile, setProfile] = useState({
    name: "",
    notes: "",
    fingerprint: {
      user_agent: "",
      platform: "Win32",
      language: "ru-RU",
      timezone: "Europe/Moscow",
      screen_width: 1920,
      screen_height: 1080,
      hardware_concurrency: 8,
      device_memory: 8,
      canvas_noise: true,
      audio_noise: true,
    },
    proxy: {
      enabled: false,
      type: "http",
      host: "",
      port: 0,
      username: "",
      password: "",
    },
    geolocation: {
      enabled: false,
      latitude: 55.7558,
      longitude: 37.6173,
      accuracy: 100,
    },
  });

  useEffect(() => {
    if (isEditing) {
      fetchProfile();
    } else {
      generateFingerprint();
    }
  }, [id]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profiles/${id}`);
      setProfile(res.data);
    } catch (e) {
      toast.error("Профиль не найден");
      navigate("/profiles");
    } finally {
      setLoading(false);
    }
  };

  const generateFingerprint = async () => {
    try {
      const res = await axios.post(`${API}/profiles/generate-fingerprint`);
      setProfile((prev) => ({ ...prev, fingerprint: res.data }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    if (!profile.name.trim()) {
      toast.error("Введите название профиля");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/profiles/${id}`, profile);
        toast.success("Профиль обновлён");
      } else {
        await axios.post(`${API}/profiles`, profile);
        toast.success("Профиль создан");
      }
      refreshStats?.();
      refreshFolders?.();
      navigate("/profiles");
    } catch (e) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const updateFingerprint = (key, value) => {
    setProfile((prev) => ({
      ...prev,
      fingerprint: { ...prev.fingerprint, [key]: value },
    }));
  };

  const updateProxy = (key, value) => {
    setProfile((prev) => ({
      ...prev,
      proxy: { ...prev.proxy, [key]: value },
    }));
  };

  const updateGeolocation = (key, value) => {
    setProfile((prev) => ({
      ...prev,
      geolocation: { ...prev.geolocation, [key]: value },
    }));
  };

  const updateOzon = (key, value) => {
    setProfile((prev) => ({
      ...prev,
      ozon: { ...(prev.ozon || {}), [key]: value },
    }));
  };

  const handleDownloadCashinout = async () => {
    setDownloadingCashinout(true);
    try {
      const res = await axios.post(`${API}/profiles/${id}/cashinout-archive`);
      if (res.data.success) {
        const byteChars = atob(res.data.archive);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename || "cashinout.zip";
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Шаблон скачан (${res.data.pages || 0} скриншотов)`);
      } else {
        toast.error(res.data.error || "Ошибка генерации");
      }
    } catch (e) {
      toast.error("Ошибка генерации шаблона");
    } finally {
      setDownloadingCashinout(false);
    }
  };

  const handleParseOzon = async (days) => {
    setParsingOzon(true);
    setParseDays(days);
    try {
      const res = await axios.post(`${API}/profiles/${id}/parse-ozon?days=${days}`);
      if (res.data.success) {
        toast.success(
          `Парсинг завершён: ${res.data.total_transactions} операций`,
          { duration: 5000, description: `Пополнения: ${res.data.deposits_count}, Выводы: ${res.data.withdrawals_count}` }
        );
        const updated = await axios.get(`${API}/profiles/${id}`);
        setProfile(updated.data);
      } else {
        const steps = res.data.steps || [];
        toast.error(res.data.error || "Ошибка парсинга", {
          duration: 6000,
          description: steps.length > 0 ? steps.slice(-2).join(" → ") : undefined,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка парсинга Ozon");
    } finally {
      setParsingOzon(false);
      setParseDays(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <ArrowsClockwise size={24} className="animate-spin text-[#2196F3]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="profile-editor-page">
      {/* Top bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <button
            onClick={() => navigate("/profiles")}
            className="icon-btn"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-semibold">
            {isEditing ? "Редактировать профиль" : "Новый профиль"}
          </h1>
        </div>
        <div className="top-bar-right">
          <button
            onClick={generateFingerprint}
            className="action-btn action-btn-outline"
            data-testid="regenerate-btn"
          >
            <ArrowsClockwise size={14} />
            Сгенерировать
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="action-btn action-btn-primary"
            data-testid="save-btn"
          >
            <FloppyDisk size={14} />
            {saving ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic info */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <h2 className="text-sm font-semibold mb-4 text-[#8A9099]">ОСНОВНАЯ ИНФОРМАЦИЯ</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#8A9099]">Название профиля</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Мой профиль"
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">Папка</Label>
                <Select
                  value={profile.folder_id || "none"}
                  onValueChange={(v) => setProfile((prev) => ({ ...prev, folder_id: v === "none" ? null : v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="folder-select">
                    <SelectValue placeholder="Выберите папку" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <Folder size={14} className="text-[#5C6370]" />
                        Основная
                      </div>
                    </SelectItem>
                    {folders?.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        <div className="flex items-center gap-2">
                          <Folder size={14} style={{ color: f.color }} />
                          {f.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#8A9099]">Заметки</Label>
              <Input
                value={profile.notes}
                onChange={(e) => setProfile((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Описание"
                className="mt-1 bg-[#0D0F12] border-[#252A33]"
                data-testid="profile-notes-input"
              />
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="fingerprint" className="bg-[#1A1D24] rounded-lg border border-[#252A33]">
            <TabsList className="w-full justify-start bg-transparent border-b border-[#252A33] rounded-none p-0">
              <TabsTrigger
                value="fingerprint"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2196F3] data-[state=active]:bg-transparent px-6 py-3"
              >
                <Gear size={16} className="mr-2" />
                Fingerprint
              </TabsTrigger>
              <TabsTrigger
                value="proxy"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2196F3] data-[state=active]:bg-transparent px-6 py-3"
              >
                <Globe size={16} className="mr-2" />
                Прокси
              </TabsTrigger>
              <TabsTrigger
                value="geolocation"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2196F3] data-[state=active]:bg-transparent px-6 py-3"
              >
                <MapPin size={16} className="mr-2" />
                Геолокация
              </TabsTrigger>
              <TabsTrigger
                value="ozon"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF6900] data-[state=active]:bg-transparent px-6 py-3"
                data-testid="ozon-tab"
              >
                <CurrencyCircleDollar size={16} className="mr-2" />
                Ozon
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#4CAF50] data-[state=active]:bg-transparent px-6 py-3"
                data-testid="history-tab"
              >
                <History size={16} className="mr-2" />
                История
              </TabsTrigger>
            </TabsList>

            <TabsContent value="fingerprint" className="p-6 space-y-4">
              <div>
                <Label className="text-xs text-[#8A9099]">User Agent</Label>
                <Textarea
                  value={profile.fingerprint.user_agent}
                  onChange={(e) => updateFingerprint("user_agent", e.target.value)}
                  className="mt-1 bg-[#0D0F12] border-[#252A33] font-mono text-xs h-20"
                  data-testid="user-agent-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-[#8A9099]">Платформа</Label>
                  <Select
                    value={profile.fingerprint.platform}
                    onValueChange={(v) => updateFingerprint("platform", v)}
                  >
                    <SelectTrigger className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Часовой пояс</Label>
                  <Select
                    value={profile.fingerprint.timezone}
                    onValueChange={(v) => updateFingerprint("timezone", v)}
                  >
                    <SelectTrigger className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Язык</Label>
                  <Input
                    value={profile.fingerprint.language}
                    onChange={(e) => updateFingerprint("language", e.target.value)}
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-[#8A9099]">Ширина экрана</Label>
                  <Input
                    type="number"
                    value={profile.fingerprint.screen_width}
                    onChange={(e) => updateFingerprint("screen_width", parseInt(e.target.value) || 0)}
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="screen-width-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Высота экрана</Label>
                  <Input
                    type="number"
                    value={profile.fingerprint.screen_height}
                    onChange={(e) => updateFingerprint("screen_height", parseInt(e.target.value) || 0)}
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="screen-height-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">CPU ядра</Label>
                  <Input
                    type="number"
                    value={profile.fingerprint.hardware_concurrency}
                    onChange={(e) => updateFingerprint("hardware_concurrency", parseInt(e.target.value) || 0)}
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Память (GB)</Label>
                  <Input
                    type="number"
                    value={profile.fingerprint.device_memory}
                    onChange={(e) => updateFingerprint("device_memory", parseInt(e.target.value) || 0)}
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  />
                </div>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={profile.fingerprint.canvas_noise}
                    onCheckedChange={(v) => updateFingerprint("canvas_noise", v)}
                    data-testid="canvas-noise-switch"
                  />
                  <Label className="text-sm">Canvas шум</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={profile.fingerprint.audio_noise}
                    onCheckedChange={(v) => updateFingerprint("audio_noise", v)}
                    data-testid="audio-noise-switch"
                  />
                  <Label className="text-sm">Audio шум</Label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="proxy" className="p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-[#252A33]">
                <Switch
                  checked={profile.proxy.enabled}
                  onCheckedChange={(v) => updateProxy("enabled", v)}
                  data-testid="proxy-enabled-switch"
                />
                <Label className="text-sm font-medium">Использовать прокси</Label>
              </div>

              {profile.proxy.enabled && (
                <div className="space-y-4 pt-2">
                  <div>
                    <Label className="text-xs text-[#8A9099]">Вставить прокси (login:pass@host:port)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="lR2YH2:EK5jeBoI1q@45.89.100.18:3000"
                        className="bg-[#0D0F12] border-[#252A33] font-mono text-xs"
                        data-testid="proxy-paste-input"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const raw = e.target.value.trim();
                            if (!raw) return;
                            // Parse: login:pass@host:port OR host:port:login:pass OR host:port
                            let username = "", password = "", host = "", port = 0;
                            if (raw.includes("@")) {
                              const [auth, server] = [raw.substring(0, raw.lastIndexOf("@")), raw.substring(raw.lastIndexOf("@") + 1)];
                              const authParts = auth.split(":");
                              username = authParts[0] || "";
                              password = authParts.slice(1).join(":") || "";
                              const serverParts = server.split(":");
                              host = serverParts[0] || "";
                              port = parseInt(serverParts[1]) || 0;
                            } else {
                              const parts = raw.split(":");
                              if (parts.length === 4) {
                                host = parts[0]; port = parseInt(parts[1]) || 0;
                                username = parts[2]; password = parts[3];
                              } else if (parts.length === 2) {
                                host = parts[0]; port = parseInt(parts[1]) || 0;
                              }
                            }
                            if (host && port) {
                              setProfile(prev => ({
                                ...prev,
                                proxy: { ...prev.proxy, host, port, username, password, enabled: true }
                              }));
                              e.target.value = "";
                              toast.success(`Прокси: ${host}:${port}`);
                            } else {
                              toast.error("Неверный формат прокси");
                            }
                          }
                        }}
                      />
                      <button
                        className="px-3 py-1 bg-[#1A5CFF] text-white text-xs rounded hover:bg-[#1A5CFF]/80"
                        data-testid="proxy-paste-btn"
                        onClick={(e) => {
                          const input = e.target.closest("div").querySelector("input");
                          const raw = (input?.value || "").trim();
                          if (!raw) return;
                          let username = "", password = "", host = "", port = 0;
                          if (raw.includes("@")) {
                            const [auth, server] = [raw.substring(0, raw.lastIndexOf("@")), raw.substring(raw.lastIndexOf("@") + 1)];
                            const authParts = auth.split(":");
                            username = authParts[0] || "";
                            password = authParts.slice(1).join(":") || "";
                            const serverParts = server.split(":");
                            host = serverParts[0] || "";
                            port = parseInt(serverParts[1]) || 0;
                          } else {
                            const parts = raw.split(":");
                            if (parts.length === 4) {
                              host = parts[0]; port = parseInt(parts[1]) || 0;
                              username = parts[2]; password = parts[3];
                            } else if (parts.length === 2) {
                              host = parts[0]; port = parseInt(parts[1]) || 0;
                            }
                          }
                          if (host && port) {
                            setProfile(prev => ({
                              ...prev,
                              proxy: { ...prev.proxy, host, port, username, password, enabled: true }
                            }));
                            if (input) input.value = "";
                            toast.success(`Прокси: ${host}:${port}`);
                          } else {
                            toast.error("Неверный формат прокси");
                          }
                        }}
                      >
                        Применить
                      </button>
                    </div>
                    <p className="text-[10px] text-[#555] mt-1">Форматы: login:pass@host:port  |  host:port:login:pass  |  host:port</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-[#8A9099]">Тип</Label>
                      <Select
                        value={profile.proxy.type}
                        onValueChange={(v) => updateProxy("type", v)}
                      >
                        <SelectTrigger className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="proxy-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROXY_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#8A9099]">Хост</Label>
                      <Input
                        value={profile.proxy.host}
                        onChange={(e) => updateProxy("host", e.target.value)}
                        placeholder="proxy.example.com"
                        className="mt-1 bg-[#0D0F12] border-[#252A33]"
                        data-testid="proxy-host-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#8A9099]">Порт</Label>
                      <Input
                        type="number"
                        value={profile.proxy.port || ""}
                        onChange={(e) => updateProxy("port", parseInt(e.target.value) || 0)}
                        placeholder="8080"
                        className="mt-1 bg-[#0D0F12] border-[#252A33]"
                        data-testid="proxy-port-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-[#8A9099]">Логин</Label>
                      <Input
                        value={profile.proxy.username}
                        onChange={(e) => updateProxy("username", e.target.value)}
                        className="mt-1 bg-[#0D0F12] border-[#252A33]"
                        data-testid="proxy-username-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#8A9099]">Пароль</Label>
                      <Input
                        type="password"
                        value={profile.proxy.password}
                        onChange={(e) => updateProxy("password", e.target.value)}
                        className="mt-1 bg-[#0D0F12] border-[#252A33]"
                        data-testid="proxy-password-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="geolocation" className="p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-[#252A33]">
                <Switch
                  checked={profile.geolocation.enabled}
                  onCheckedChange={(v) => updateGeolocation("enabled", v)}
                  data-testid="geolocation-enabled-switch"
                />
                <Label className="text-sm font-medium">Подмена геолокации</Label>
              </div>

              {profile.geolocation.enabled && (
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div>
                    <Label className="text-xs text-[#8A9099]">Широта</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={profile.geolocation.latitude}
                      onChange={(e) => updateGeolocation("latitude", parseFloat(e.target.value) || 0)}
                      className="mt-1 bg-[#0D0F12] border-[#252A33]"
                      data-testid="latitude-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A9099]">Долгота</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={profile.geolocation.longitude}
                      onChange={(e) => updateGeolocation("longitude", parseFloat(e.target.value) || 0)}
                      className="mt-1 bg-[#0D0F12] border-[#252A33]"
                      data-testid="longitude-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A9099]">Точность (м)</Label>
                    <Input
                      type="number"
                      value={profile.geolocation.accuracy}
                      onChange={(e) => updateGeolocation("accuracy", parseInt(e.target.value) || 0)}
                      className="mt-1 bg-[#0D0F12] border-[#252A33]"
                      data-testid="accuracy-input"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="ozon" className="p-6 space-y-4" data-testid="ozon-tab-content">
              <h3 className="text-sm font-semibold text-[#8A9099] mb-2">ДАННЫЕ OZON ПРОФИЛЯ</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#8A9099]">ФИО</Label>
                  <Input
                    value={profile.ozon?.fullname || ""}
                    onChange={(e) => updateOzon("fullname", e.target.value)}
                    placeholder="Иванов Иван Иванович"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-fullname-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Телефон</Label>
                  <Input
                    value={profile.ozon?.phone || ""}
                    onChange={(e) => updateOzon("phone", e.target.value)}
                    placeholder="+7 (999) 123-45-67"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-phone-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#8A9099]">Email</Label>
                  <Input
                    value={profile.ozon?.email || ""}
                    onChange={(e) => updateOzon("email", e.target.value)}
                    placeholder="генерируется при парсинге"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Пол</Label>
                  <Select
                    value={profile.ozon?.gender || "auto"}
                    onValueChange={(v) => updateOzon("gender", v)}
                  >
                    <SelectTrigger className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="ozon-gender-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Авто (по имени)</SelectItem>
                      <SelectItem value="male">Мужской</SelectItem>
                      <SelectItem value="female">Женский</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-[#8A9099]">PIN-код</Label>
                  <Input
                    value={profile.ozon?.pincode || ""}
                    onChange={(e) => updateOzon("pincode", e.target.value)}
                    placeholder="1234"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-pincode-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Cashinout ID</Label>
                  <Input
                    value={profile.ozon?.cashinout_id || ""}
                    onChange={(e) => updateOzon("cashinout_id", e.target.value ? parseInt(e.target.value) || 0 : 0)}
                    placeholder="123456"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-cashinout-id-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A9099]">Баланс (для шаблона)</Label>
                  <Input
                    value={profile.ozon?.balance || ""}
                    onChange={(e) => updateOzon("balance", e.target.value)}
                    placeholder="10000"
                    className="mt-1 bg-[#0D0F12] border-[#252A33]"
                    data-testid="ozon-balance-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">Город</Label>
                <Input
                  value={profile.ozon?.city || ""}
                  onChange={(e) => updateOzon("city", e.target.value)}
                  placeholder="Москва"
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="ozon-city-input"
                />
              </div>

              {/* Parse + Download */}
              {isEditing && (
                <div className="pt-4 border-t border-[#252A33] space-y-4">
                  <h3 className="text-sm font-semibold text-[#8A9099]">ПАРСИНГ OZON</h3>
                  <p className="text-xs text-[#5C6370] mb-1">
                    Парсит из запущенного браузера: ФИО, телефон, баланс и операции.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[15, 30, 60, 100].map((d) => (
                      <button
                        key={d}
                        onClick={() => handleParseOzon(d)}
                        disabled={parsingOzon}
                        className="action-btn"
                        style={{ backgroundColor: parsingOzon && parseDays === d ? "#555" : "#FF6900", color: "#fff", border: "none" }}
                        data-testid={`parse-ozon-${d}-btn`}
                      >
                        <ArrowsClockwise size={14} className={parsingOzon && parseDays === d ? "animate-spin" : ""} />
                        {parsingOzon && parseDays === d ? "Парсинг..." : `${d} дней`}
                      </button>
                    ))}
                  </div>

                  {profile.ozon?.transactions?.length > 0 && (
                    <div className="text-xs text-[#8A9099] mt-1">
                      Операций: <span className="text-white font-medium">{profile.ozon.transactions.length}</span>
                      {" "}(пополнения: {profile.ozon.transactions.filter(t => t.type === "deposit").length},
                      выводы: {profile.ozon.transactions.filter(t => t.type === "withdrawal").length})
                    </div>
                  )}

                  <h3 className="text-sm font-semibold text-[#8A9099] mt-4">ШАБЛОН CASHINOUT</h3>
                  <button
                    onClick={handleDownloadCashinout}
                    disabled={downloadingCashinout}
                    className="action-btn action-btn-primary"
                    data-testid="download-cashinout-btn"
                  >
                    <DownloadSimple size={16} />
                    {downloadingCashinout ? "Генерация..." : "Скачать шаблон Cashinout"}
                  </button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="p-6" data-testid="history-tab-content">
              {(() => {
                const txs = profile.ozon?.transactions || [];
                const deposits = txs.filter(t => t.type === "deposit");
                const withdrawals = txs.filter(t => t.type === "withdrawal");
                if (txs.length === 0) {
                  return (
                    <div className="text-center py-12 text-[#5C6370]">
                      <History size={40} className="mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Нет операций. Используйте парсинг во вкладке Ozon.</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 text-sm text-[#8A9099]">
                      <span>Всего: <span className="text-white font-medium">{txs.length}</span></span>
                      <span>Пополнения: <span className="text-green-400 font-medium">{deposits.length}</span></span>
                      <span>Выводы: <span className="text-red-400 font-medium">{withdrawals.length}</span></span>
                    </div>

                    {deposits.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-green-400 mb-2">Пополнения</h3>
                        <div className="bg-[#0D0F12] rounded-lg border border-[#252A33] overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#252A33] text-[#5C6370] text-xs">
                                <th className="text-left px-3 py-2">Дата</th>
                                <th className="text-left px-3 py-2">Время</th>
                                <th className="text-right px-3 py-2">Сумма</th>
                                <th className="text-left px-3 py-2">Описание</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deposits.map((tx, i) => (
                                <tr key={i} className="border-b border-[#1a1d24] text-xs">
                                  <td className="px-3 py-1.5 text-[#C8CCD4]">{tx.date}</td>
                                  <td className="px-3 py-1.5 text-[#8A9099]">{tx.time}</td>
                                  <td className="px-3 py-1.5 text-right text-green-400 font-medium">+{(tx.amount || 0).toLocaleString("ru-RU")} ₽</td>
                                  <td className="px-3 py-1.5 text-[#8A9099] truncate max-w-[200px]">{tx.method || ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {withdrawals.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-red-400 mb-2">Выводы</h3>
                        <div className="bg-[#0D0F12] rounded-lg border border-[#252A33] overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#252A33] text-[#5C6370] text-xs">
                                <th className="text-left px-3 py-2">Дата</th>
                                <th className="text-left px-3 py-2">Время</th>
                                <th className="text-right px-3 py-2">Сумма</th>
                                <th className="text-left px-3 py-2">Описание</th>
                              </tr>
                            </thead>
                            <tbody>
                              {withdrawals.map((tx, i) => (
                                <tr key={i} className="border-b border-[#1a1d24] text-xs">
                                  <td className="px-3 py-1.5 text-[#C8CCD4]">{tx.date}</td>
                                  <td className="px-3 py-1.5 text-[#8A9099]">{tx.time}</td>
                                  <td className="px-3 py-1.5 text-right text-red-400 font-medium">-{(tx.amount || 0).toLocaleString("ru-RU")} ₽</td>
                                  <td className="px-3 py-1.5 text-[#8A9099] truncate max-w-[200px]">{tx.method || ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;
